import { useState, type FormEvent } from "react";

import { Loader2, MailCheck } from "lucide-react";
import { Link } from "react-router-dom";

import { GradientScene, SceneError } from "@/components/auth/GradientScene";
import { ApiError } from "@/lib/api-client";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch("/internal/v1/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok && res.status !== 200) {
        const data = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(res.status, data.message ?? res.statusText);
      }
      setSent(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many requests. Please wait a moment and try again."
          : "Could not reach the server. Check your connection and try again.",
      );
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <GradientScene>
        <div className="flex justify-center">
          <div className="rounded-full bg-white/15 p-3">
            <MailCheck className="h-7 w-7 text-white" />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-white">Check your email</h2>
          {/* ⚠ Deliberately says nothing about whether that address has an account. The
              server returns one identical message for every outcome; contradicting it here
              would hand anyone a way to enumerate who works for Unifiber's contractors. */}
          <p className="mt-2 text-sm text-white/65">
            If an account exists for that address, a reset link is on its way. The link is good
            for 24 hours and can be used once.
          </p>
        </div>
        <Link
          to="/"
          className="glass-outline-btn block rounded-lg py-2.5 text-center text-sm font-medium text-white/90"
        >
          Back to sign in
        </Link>
      </GradientScene>
    );
  }

  return (
    <GradientScene>
      <div>
        <h2 className="text-xl font-bold text-white">Reset your password</h2>
        <p className="mt-1 text-sm text-white/60">
          Enter your email address and we will send you a link.
        </p>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        <SceneError message={error} />
        <div>
          <label htmlFor="reset-email" className="mb-1.5 block text-sm font-medium text-white/80">
            Email address
          </label>
          <input
            id="reset-email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className="glass-input w-full rounded-lg px-4 py-2.5 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="gradient-btn flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? "Sending…" : "Send reset link"}
        </button>
      </form>

      <Link
        to="/"
        className="block text-center text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
      >
        Back to sign in
      </Link>
    </GradientScene>
  );
}
